import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { StatusFilter } from "@/components/common/Filters/StatusFilter"
import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { ComputedProductStatus, type ProductListFilter } from "@/graphql/codegen/graphql"
import { SupplierFilter } from "@/screens/Products/components/filters/SupplierFilter"

const statusList: ComputedProductStatus[] = [
  ComputedProductStatus.Active,
  ComputedProductStatus.PendingApproval,
  ComputedProductStatus.Discontinued,
]

const labelMap = {
  [ComputedProductStatus.Active]: "Active",
  [ComputedProductStatus.Discontinued]: "Discontinued",
  [ComputedProductStatus.PendingApproval]: "Pending",
  [ComputedProductStatus.Inactive]: "Inactive",
}

interface FilterDrawerProps {
  showFilterDrawer: boolean
  toggleDrawer: (open: boolean) => void
}

type FormValues = ProductListFilter & {
  computedProductStatuses?: ComputedProductStatus[]
  companyIds?: string[]
}

export const FilterDrawer: FC<FilterDrawerProps> = ({ toggleDrawer, showFilterDrawer }) => {
  const navigate = useNavigate({ from: "/app/products" })
  const currentSearch = useSearch({
    from: "/app/products/",
    strict: true,
  })

  const { computedProductStatuses = [], companyIds = [], field, order } = currentSearch

  const methods = useForm<FormValues>({
    values: {
      computedProductStatuses,
      companyIds,
    },
  })

  const handleClearFilters = () => {
    methods.reset()
    navigate({
      search: (prev: Record<string, any>) => ({
        ...prev,
        computedProductStatuses: [ComputedProductStatus.Active],
        companyIds: undefined,
        field,
        order,
      }),
      replace: true,
    })
  }

  const handleFilterChange = (name: keyof FormValues, value: unknown) => {
    navigate({
      search: (prev: Record<string, any>) => {
        const newFilter = { ...prev }

        if (Array.isArray(value)) {
          ;(newFilter[name] as (string[] | ComputedProductStatus)[]) = value

          if (value.length === 0) {
            newFilter[name] = undefined
          }
        }

        return newFilter
      },

      replace: true,
    })
  }

  return (
    <SearchFilterDrawer
      toggleDrawer={() => toggleDrawer(!showFilterDrawer)}
      open={showFilterDrawer}
      clear={handleClearFilters}
    >
      <FormProvider {...methods}>
        <Box className='w-96 flex-none'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Product Status
          </Typography>
          <Divider />
          <StatusFilter
            name='computedProductStatuses'
            data={statusList}
            control={methods.control}
            onFilterChange={handleFilterChange}
            labelMap={labelMap}
          />
        </Box>

        <Box className='grow overflow-hidden'>
          <SupplierFilter onFilterChange={handleFilterChange} />
        </Box>
      </FormProvider>
    </SearchFilterDrawer>
  )
}
